import * as Yup from "yup";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { LoadingButton } from "@mui/lab";
import {
    Container,
    Grid,
    Card,
    Stack,
    Typography,
    Alert,
    FormControlLabel,
    Checkbox,
    TextField,
    Link,
    IconButton,
    Tooltip,
    Box,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useSettingsContext } from "../components/settings";
import { useSnackbar } from "../components/snackbar";
import FormProvider, { RHFTextField } from "../components/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import {
    initiateDataExport,
    InitiateDataExportResponse,
} from "../controllers/dataExport";
import axios from "axios";

const DATA_TYPE_OPTIONS = [
    { value: "weight", label: "Weight Logs" },
    { value: "meals", label: "Meal Logs" },
    { value: "checkins", label: "Nutrition Check-Ins" },
    { value: "workouts", label: "Workout Logs" },
    { value: "photos", label: "Progress Photos" },
];

interface FormValues {
    userId: string;
    startDate: string;
    endDate: string;
}

export default function DataExport() {
    const { themeStretch } = useSettingsContext();
    const { enqueueSnackbar } = useSnackbar();

    const [selectedTypes, setSelectedTypes] = useState<string[]>(
        DATA_TYPE_OPTIONS.map((o) => o.value)
    );
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<InitiateDataExportResponse | null>(null);
    const [copied, setCopied] = useState(false);

    const defaultValues: FormValues = {
        userId: "",
        startDate: "",
        endDate: "",
    };

    const schema = Yup.object().shape({
        userId: Yup.string().required("User ID is required"),
        startDate: Yup.string(),
        endDate: Yup.string(),
    });

    const methods = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const {
        reset,
        handleSubmit,
        formState: { isValid },
    } = methods;

    const handleToggleType = (value: string) => {
        setSelectedTypes((prev) =>
            prev.includes(value)
                ? prev.filter((v) => v !== value)
                : [...prev, value]
        );
    };

    const handleSelectAll = () => {
        if (selectedTypes.length === DATA_TYPE_OPTIONS.length) {
            setSelectedTypes([]);
        } else {
            setSelectedTypes(DATA_TYPE_OPTIONS.map((o) => o.value));
        }
    };

    const handleCopy = async () => {
        if (result?.downloadPageUrl) {
            await navigator.clipboard.writeText(result.downloadPageUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const onSubmit = async (data: FormValues) => {
        if (selectedTypes.length === 0) {
            enqueueSnackbar("Please select at least one data type.", {
                variant: "warning",
            });
            return;
        }

        setIsLoading(true);
        setResult(null);
        setCopied(false);

        try {
            const response = await initiateDataExport({
                userId: data.userId,
                dataTypes: selectedTypes,
                startDate: data.startDate || undefined,
                endDate: data.endDate || undefined,
            });

            setResult(response);
            enqueueSnackbar("Data export initiated successfully!");
        } catch (error) {
            console.error(error);
            let errorMessage = "Failed to initiate data export.";

            if (axios.isAxiosError(error) && error.response?.data) {
                const responseData = error.response.data;
                if (typeof responseData === "string") {
                    errorMessage = responseData;
                } else {
                    errorMessage =
                        responseData.data?.message ||
                        responseData.message ||
                        responseData.error ||
                        errorMessage;
                }
            }

            enqueueSnackbar(errorMessage, { variant: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>User Data Export | Admin Portal</title>
            </Helmet>

            <Container maxWidth={themeStretch ? false : "xl"}>
                <Typography variant="h3" component="h1" paragraph>
                    User Data Export
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        {result && (
                            <Alert
                                severity="success"
                                icon={<CheckCircleOutlineIcon />}
                                sx={{ mb: 3 }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 1 }}
                                >
                                    Export Initiated
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1.5 }}>
                                    {result.message}
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        bgcolor: "action.hover",
                                        borderRadius: 1,
                                        p: 1.5,
                                    }}
                                >
                                    <Link
                                        href={result.downloadPageUrl}
                                        target="_blank"
                                        rel="noopener"
                                        sx={{
                                            flex: 1,
                                            wordBreak: "break-all",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        {result.downloadPageUrl}
                                    </Link>
                                    <Tooltip title={copied ? "Copied!" : "Copy link"}>
                                        <IconButton
                                            size="small"
                                            onClick={handleCopy}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 1, display: "block" }}
                                >
                                    Share this link with the user. They will need the
                                    verification code sent to their email to download.
                                </Typography>
                            </Alert>
                        )}

                        <FormProvider
                            methods={methods}
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <Card sx={{ p: 3 }}>
                                <Stack spacing={3}>
                                    <RHFTextField
                                        name="userId"
                                        label="User ID (sub)"
                                        required
                                        disabled={isLoading}
                                    />

                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 1 }}
                                        >
                                            Data Types
                                        </Typography>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={
                                                        selectedTypes.length ===
                                                        DATA_TYPE_OPTIONS.length
                                                    }
                                                    indeterminate={
                                                        selectedTypes.length > 0 &&
                                                        selectedTypes.length <
                                                            DATA_TYPE_OPTIONS.length
                                                    }
                                                    onChange={handleSelectAll}
                                                    disabled={isLoading}
                                                />
                                            }
                                            label="Select All"
                                            sx={{ mb: 0.5 }}
                                        />
                                        <Stack
                                            direction="row"
                                            flexWrap="wrap"
                                            sx={{ pl: 1 }}
                                        >
                                            {DATA_TYPE_OPTIONS.map((option) => (
                                                <FormControlLabel
                                                    key={option.value}
                                                    control={
                                                        <Checkbox
                                                            checked={selectedTypes.includes(
                                                                option.value
                                                            )}
                                                            onChange={() =>
                                                                handleToggleType(
                                                                    option.value
                                                                )
                                                            }
                                                            disabled={isLoading}
                                                        />
                                                    }
                                                    label={option.label}
                                                    sx={{
                                                        minWidth: 180,
                                                        mr: 2,
                                                    }}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>

                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            label="Start Date (optional)"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                            disabled={isLoading}
                                            {...methods.register("startDate")}
                                        />
                                        <TextField
                                            label="End Date (optional)"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                            disabled={isLoading}
                                            {...methods.register("endDate")}
                                        />
                                    </Stack>
                                </Stack>
                            </Card>

                            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                                <LoadingButton
                                    fullWidth
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    loading={isLoading}
                                    disabled={
                                        !isValid ||
                                        selectedTypes.length === 0 ||
                                        isLoading
                                    }
                                >
                                    Initiate Export
                                </LoadingButton>
                            </Stack>
                        </FormProvider>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
}
